import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

import { Typography } from "antd";

import { cn } from "lib/utils";
import { toast } from "utils/toast";
import { useGetOrganizationCredits } from "hooks/useOrganizations";
import {
  useGetQuotaOrders,
  useGetQuotaPackages,
  useGetQuotaTransactions,
  useExportQuotasCSV,
} from "hooks/useQuotas";

import type { PaginationTable } from "js/components/types";

import type { Advisor, Credits } from "js/pages/Organization/types";
import type { OrderFilters, TransactionFilters } from "./types";

import ActivityFeed from "./components/ActivityFeed";
import BalanceHero from "./components/BalanceHero";
import PurchaseSheet from "./components/PurchaseSheet";
import { QuotasSkeleton } from "./components/QuotasSkeleton";
import TeamSection from "./components/TeamSection";

const { Title } = Typography;
type QuoteType = "PF" | "PM";

const QuotasPage = () => {
  const { userData } = useSelector((state: any) => state.entities.user);
  const isOwner = userData?.role === "Owner";

  const {
    data: rawCreditData,
    isLoading: creditsLoading,
    isError: creditsError,
  } = useGetOrganizationCredits();
  const {
    data: packagesData,
    isLoading: packagesLoading,
    isError: packagesError,
  } = useGetQuotaPackages();

  const [purchaseSheetOpen, setPurchaseSheetOpen] = useState(false);
  const [rechargeType, setRechargeType] = useState<QuoteType>("PF");
  const [expandedPanel, setExpandedPanel] = useState<"team" | "activity" | null>(null);
  const [selectedMemberEmail, setSelectedMemberEmail] = useState<string | null>(null);
  const [quoteTypeFilter, setQuoteTypeFilter] = useState<"PF" | "PM" | null>(null);
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [activityPage, setActivityPage] = useState(1);
  const [activeTab, setActiveTab] = useState<"all" | "purchases" | "consumption" | "distribution">("all");

  const ordersPagination = useMemo<PaginationTable>(
    () => ({ current: activityPage, pageSize: 20, total: 0 }),
    [activityPage],
  );
  const transactionsPagination = useMemo<PaginationTable>(
    () => ({ current: activityPage, pageSize: 20, total: 0 }),
    [activityPage],
  );

  const orderFilters = useMemo<OrderFilters | undefined>(() => {
    const filters: OrderFilters = {};
    if (quoteTypeFilter) filters.quote_type = quoteTypeFilter;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [quoteTypeFilter, dateFrom, dateTo]);

  const transactionFilters = useMemo<TransactionFilters | undefined>(() => {
    const filters: TransactionFilters = {};
    if (quoteTypeFilter) filters.quote_type = quoteTypeFilter;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    if (activeTab === "consumption") filters.transaction_type = "CONSUMPTION";
    if (activeTab === "distribution") filters.transaction_type = "DISTRIBUTION";
    if (activeTab === "purchases") filters.transaction_type = "PURCHASE";
    if (selectedMemberEmail) filters.performed_by = selectedMemberEmail;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [quoteTypeFilter, dateFrom, dateTo, activeTab, selectedMemberEmail]);

  const { data: ordersData, isLoading: ordersLoading, isError: ordersError } =
    useGetQuotaOrders(ordersPagination, orderFilters);
  const { data: transactionsData, isLoading: transactionsLoading, isError: transactionsError } =
    useGetQuotaTransactions(transactionsPagination, transactionFilters);

  const exportCSV = useExportQuotasCSV();

  const credits: Credits = rawCreditData?.organization?.quotes ?? {
    pf: 0,
    pm: 0,
  };
  const packages = packagesData?.results ?? [];
  const orders = ordersData?.results ?? [];
  const transactions = transactionsData?.results ?? [];

  const advisors: Advisor[] = useMemo(() => {
    if (!rawCreditData?.advisors) return [];
    return rawCreditData.advisors.map((advisor: any) => ({
      id: advisor.id,
      name: advisor.name ?? advisor.full_name ?? "",
      first_surname: advisor.first_surname ?? "",
      email: advisor.email ?? "",
      role: advisor.role ?? "",
      credits: advisor.quotes ?? advisor.credits ?? { pf: 0, pm: 0 },
    }));
  }, [rawCreditData]);

  const handleExport = () => {
    const filters: Record<string, string> = {};
    if (quoteTypeFilter) filters.quote_type = quoteTypeFilter;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    if (selectedMemberEmail) filters.performed_by = selectedMemberEmail;
    if (activeTab === "consumption") filters.transaction_type = "CONSUMPTION";
    if (activeTab === "distribution") filters.transaction_type = "DISTRIBUTION";
    if (activeTab === "purchases") filters.transaction_type = "PURCHASE";

    const type = activeTab === "all" ? "all" : activeTab === "purchases" ? "orders" : "transactions";
    exportCSV.mutate({ type, filters });
  };

  const handleRecharge = (type: QuoteType) => {
    setRechargeType(type);
    setPurchaseSheetOpen(true);
  };

  const handleQuoteTypeChange = (qt: "PF" | "PM" | null) => {
    setQuoteTypeFilter(qt);
    setActivityPage(1);
  };

  const handleDateFilterChange = (from: string | undefined, to: string | undefined) => {
    setDateFrom(from);
    setDateTo(to);
    setActivityPage(1);
  };

  const handleTabChange = (tab: "all" | "purchases" | "consumption" | "distribution") => {
    setActiveTab(tab);
    setActivityPage(1);
  };

  const hasActiveFilters =
    quoteTypeFilter !== null ||
    dateFrom !== undefined ||
    dateTo !== undefined ||
    activeTab !== "all" ||
    activityPage !== 1 ||
    selectedMemberEmail !== null;

  const resetExpandedFilters = () => {
    setQuoteTypeFilter(null);
    setDateFrom(undefined);
    setDateTo(undefined);
    setActiveTab("all");
    setActivityPage(1);
    setSelectedMemberEmail(null);
  };

  const handleToggleActivity = () => {
    setExpandedPanel((p) => {
      if (p === "activity") {
        // Collapsing — reset filters if any were active
        if (hasActiveFilters) {
          resetExpandedFilters();
          toast.info("Filtros restablecidos");
        }
        return null;
      }
      return "activity";
    });
  };

  const handleToggleTeam = () => {
    setExpandedPanel((p) => (p === "team" ? null : "team"));
  };

  const isAnyLoading = creditsLoading || packagesLoading;
  if (isAnyLoading && !rawCreditData && !packagesData) {
    return <QuotasSkeleton />;
  }

  if (creditsError || packagesError) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-muted-foreground">
          Error al cargar los datos. Intenta de nuevo.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 xl:h-full xl:min-h-0">
      {/* ── Page Header ──────────────────────────────────────── */}
      <div className="shrink-0">
        <Title level={1} style={{ marginBottom: 0 }}>Cuotas</Title>
        <p className="mt-1 text-sm text-muted-foreground">
          Administra los créditos de consulta de tu organización
        </p>
      </div>

      {/* ── Credit Cards (PF + PM) ───────────────────────────── */}
      <AnimatePresence initial={false}>
        {!expandedPanel && (
          <motion.div
            key="balance-hero"
            className="shrink-0"
            initial={{ opacity: 0, height: 0, overflow: "hidden" }}
            animate={{
              opacity: 1,
              height: "auto",
              transitionEnd: { overflow: "visible" },
            }}
            exit={{ overflow: "hidden", opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <BalanceHero
              credits={credits}
              advisors={advisors}
              isLoading={creditsLoading}
              isOwner={isOwner}
              onRecharge={handleRecharge}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bottom Grid: Team + Activity Feed ────────────────── */}
      <div className="grid grid-cols-1 gap-6 transition-all duration-300 xl:grid-cols-2 xl:flex-1 xl:min-h-0 xl:grid-rows-[1fr]">
        <div
          className={cn(
            "h-[400px] xl:h-full xl:min-h-0 transition-all duration-300",
            expandedPanel === "team" && "xl:col-span-2",
            expandedPanel === "activity" && "hidden",
          )}
        >
          <TeamSection
            advisors={advisors}
            orgCredits={credits}
            isLoading={creditsLoading}
            isOwner={isOwner}
            isExpanded={expandedPanel === "team"}
            onToggleExpand={handleToggleTeam}
            onMemberClick={(email) => {
              setSelectedMemberEmail((prev) => (prev === email ? null : email));
              setActivityPage(1);
            }}
          />
        </div>

        <div
          className={cn(
            "h-[400px] xl:h-full xl:min-h-0 transition-all duration-300",
            expandedPanel === "activity" && "xl:col-span-2",
            expandedPanel === "team" && "hidden",
          )}
        >
          <ActivityFeed
            orders={orders}
            transactions={transactions}
            isLoadingOrders={ordersLoading}
            isLoadingTransactions={transactionsLoading}
            isErrorOrders={ordersError}
            isErrorTransactions={transactionsError}
            isExpanded={expandedPanel === "activity"}
            onToggleExpand={handleToggleActivity}
            filterByEmail={selectedMemberEmail}
            onClearFilter={() => { setSelectedMemberEmail(null); setActivityPage(1); }}
            quoteTypeFilter={quoteTypeFilter}
            onQuoteTypeFilterChange={handleQuoteTypeChange}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFilterChange={handleDateFilterChange}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            currentPage={activityPage}
            onPageChange={setActivityPage}
            onExport={handleExport}
            isExporting={exportCSV.isPending}
          />
        </div>
      </div>

      {/* ── Purchase Sheet (Owner only) ──────────────────────── */}
      {isOwner && (
        <PurchaseSheet
          open={purchaseSheetOpen}
          onOpenChange={setPurchaseSheetOpen}
          quoteType={rechargeType}
          packages={packages}
        />
      )}
    </div>
  );
};

export default QuotasPage;
